/**
 * Changelog parser — turns `docs/changelog.md` into the JSON the staff app's
 * "What's new" panel renders.
 *
 * Pure + dependency-free so it can run both under Node (the build script at
 * `apps/web/scripts/build_changelog.ts`) and in vitest.
 *
 * Format contract (see the header of docs/changelog.md):
 *   ## YYYY-MM-DD — Title          → starts a release (em dash or hyphen)
 *   - [tag] [tag] text             → one change; tags from CHANGELOG_TAGS
 *   anything else                  → ignored (prose, other headings, blanks)
 *
 * Level-2 headings that don't match the date pattern (e.g. the "Format"
 * section in the source file) close the current release without opening a
 * new one, so bullets under them are dropped rather than mis-attributed.
 */

export const CHANGELOG_TAGS = [
  'boarding',
  'daycare',
  'grooming',
  'payments',
  'portal',
  'settings',
  'staff',
] as const;

export type ChangelogTag = (typeof CHANGELOG_TAGS)[number];

export interface ChangelogItem {
  tags: ChangelogTag[];
  text: string;
}

export interface ChangelogEntry {
  /** ISO date `YYYY-MM-DD`. */
  date: string;
  title: string;
  items: ChangelogItem[];
}

export interface ParsedChangelog {
  entries: ChangelogEntry[];
  /** Non-fatal problems (unknown tags, empty releases) for the build log. */
  warnings: string[];
}

const HEADING_RE = /^##\s+(\d{4}-\d{2}-\d{2})\s*(?:—|–|-)\s*(.+?)\s*$/;
const ANY_H2_RE = /^##\s/;
// A bare `-` (no body) still counts as a bullet so it can be reported as empty.
const BULLET_RE = /^[-*](?:\s+(.*)|\s*)$/;
const TAG_RE = /^\[([a-z_-]+)\]\s*/i;

function isValidDate(iso: string): boolean {
  const [y, m, d] = iso.split('-').map((n) => Number.parseInt(n, 10));
  if (!y || !m || !d) return false;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

function isTag(value: string): value is ChangelogTag {
  return (CHANGELOG_TAGS as readonly string[]).includes(value);
}

/** Split leading `[tag]` prefixes off a bullet body. Unknown tags stay in the text. */
export function parseBullet(
  raw: string,
  warn: (msg: string) => void = () => undefined,
): ChangelogItem {
  let rest = raw.trim();
  const tags: ChangelogTag[] = [];
  for (;;) {
    const m = TAG_RE.exec(rest);
    if (!m) break;
    const tag = (m[1] ?? '').toLowerCase();
    if (!isTag(tag)) {
      warn(`unknown tag [${tag}] in "${raw.trim()}"`);
      break;
    }
    if (!tags.includes(tag)) tags.push(tag);
    rest = rest.slice(m[0].length);
  }
  return { tags, text: rest.trim() };
}

export function parseChangelog(markdown: string): ParsedChangelog {
  const warnings: string[] = [];
  const warn = (msg: string) => warnings.push(msg);
  const entries: ChangelogEntry[] = [];
  let current: ChangelogEntry | null = null;

  const lines = markdown.replace(/\r\n?/g, '\n').split('\n');
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? '';
    const heading = HEADING_RE.exec(line);
    if (heading) {
      const date = heading[1] ?? '';
      const title = heading[2] ?? '';
      if (!isValidDate(date)) {
        warn(`line ${i + 1}: invalid date "${date}" — section skipped`);
        current = null;
        continue;
      }
      current = { date, title, items: [] };
      entries.push(current);
      continue;
    }
    if (ANY_H2_RE.test(line)) {
      // A non-release H2 (format notes, etc.) ends the current release.
      current = null;
      continue;
    }
    if (!current) continue;
    const bullet = BULLET_RE.exec(line);
    if (!bullet) continue;
    const item = parseBullet(bullet[1] ?? '', warn);
    if (item.text.length === 0) {
      warn(`line ${i + 1}: empty bullet skipped`);
      continue;
    }
    current.items.push(item);
  }

  for (const e of entries) {
    if (e.items.length === 0) warn(`release ${e.date} "${e.title}" has no items`);
  }

  // Newest first regardless of authoring order; stable for equal dates.
  const sorted = entries
    .map((e, idx) => ({ e, idx }))
    .sort((a, b) => (a.e.date === b.e.date ? a.idx - b.idx : a.e.date < b.e.date ? 1 : -1))
    .map(({ e }) => e);

  return { entries: sorted, warnings };
}

/** The most recent release date in a parsed changelog, or null when empty. */
export function newestChangelogDate(entries: ReadonlyArray<ChangelogEntry>): string | null {
  let newest: string | null = null;
  for (const e of entries) {
    if (newest === null || e.date > newest) newest = e.date;
  }
  return newest;
}
